import { Check, Save, UserPlus } from '@tamagui/lucide-icons';
import { Form } from '@unistocks/components/form/form';
import { MultiStepForm } from '@unistocks/components/form/multi-step-form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { SheetProps } from '@unistocks/components/sheet/sheet';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { IdentityRoleDto, IdentityUserCreateDto } from '@unistocks/proxy/axios-client';
import {
  useUserCreateMutation,
  useUserGetAssignableRolesQuery
} from '@unistocks/proxy/axios-client/UserQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import React, { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Checkbox, Label, Separator } from 'tamagui';
import { z } from 'zod';

type CreateUserSheetProps = Omit<SheetProps, 'children' | 'snapPoints' | 'snapPointsMode'> & {
  onCreated?: () => void;
};

const initialValues: IdentityUserCreateDto = {
  userName: '',
  name: null,
  surname: null,
  email: '',
  phoneNumber: null,
  isActive: true,
  lockoutEnabled: false,
  roleNames: [],
  password: '',
  extraProperties: {}
};

const schemaFactory = () =>
  z.object({
    userName: requiredSchema(z.string(), t('AbpIdentity::UserName')),
    name: z.string().nullish(),
    surname: z.string().nullish(),
    email: requiredSchema(z.email(), t('AbpIdentity::DisplayName:Email')),
    phoneNumber: z.string().nullish(),
    isActive: z.boolean().default(true),
    lockoutEnabled: z.boolean().default(false),
    roleNames: z.array(z.string()).default([]),
    password: requiredSchema(
      z.string().min(6, t('::ValidationError:Min', { 0: t('AbpIdentity::Password'), 1: 6 })),
      t('AbpIdentity::Password')
    ),
    extraProperties: z.record(z.string(), z.any()).default({})
  });

export const CreateUserSheet: React.FC<CreateUserSheetProps> = ({
  open,
  onOpenChange,
  onCreated,
  ...sheetProps
}) => {
  const formRef = useRef<UseFormReturn<IdentityUserCreateDto>>(null);
  const schema = schemaFactory();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [step, setStep] = useState(0);
  const [prevOpen, setPrevOpen] = useState(open);

  if (prevOpen !== open) {
    setPrevOpen(open);

    if (!open) {
      setSelectedRoles([]);
      setStep(0);
    }
  }

  const { data: assignableRoles, isPending: isLoadingRoles } = useUserGetAssignableRolesQuery({
    enabled: open
  });

  const { mutateAsync: createUser } = useUserCreateMutation({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      onCreated?.();
      onOpenChange?.(false);
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
    }
  });

  useEffect(() => {
    if (!open) {
      formRef.current?.reset(initialValues);
    }
  }, [open]);

  const handleSubmit = (values: IdentityUserCreateDto) =>
    createUser({
      ...values,
      roleNames: selectedRoles,
      extraProperties: values.extraProperties ?? {}
    });

  const toggleRole = (roleName: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleName) ? prev.filter(role => role !== roleName) : [...prev, roleName]
    );
  };

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      headerTitle={t('AbpIdentity::NewUser')}
      headerIcon={<UserPlus color={'$unistocks'} />}
      {...sheetProps}
    >
      <MultiStepForm
        ref={formRef}
        schema={schema}
        initialValues={initialValues}
        step={step}
        onStepChange={setStep}
        triggerText={t('AbpUi::Save')}
        triggerIcon={Save}
        onSubmit={handleSubmit}
        onCancel={() => onOpenChange?.(false)}
      >
        <MultiStepForm.Step label={t('AbpIdentity::UserInformations')}>
          <Form.Input
            name="userName"
            isRequired
            label={t('AbpIdentity::UserName')}
            placeholder={t('AbpIdentity::UserName')}
          />
          <Form.Input
            name="name"
            label={t('AbpIdentity::DisplayName:Name')}
            placeholder={t('AbpIdentity::DisplayName:Name')}
          />
          <Form.Input
            name="surname"
            label={t('AbpIdentity::DisplayName:Surname')}
            placeholder={t('AbpIdentity::DisplayName:Surname')}
          />
          <Form.Input
            name="email"
            isRequired
            label={t('AbpIdentity::DisplayName:Email')}
            placeholder={t('AbpIdentity::DisplayName:Email')}
            keyboardType="email-address"
          />
          <Form.Input
            name="phoneNumber"
            label={t('AbpIdentity::DisplayName:PhoneNumber')}
            placeholder={t('AbpIdentity::DisplayName:PhoneNumber')}
            keyboardType="phone-pad"
          />
          <Form.Input
            name="password"
            isRequired
            label={t('AbpIdentity::Password')}
            placeholder={t('AbpIdentity::Password')}
            type={'password'}
          />
          <Form.Checkbox name="isActive" label={t('AbpIdentity::DisplayName:IsActive')} />
          <Form.Checkbox
            name="lockoutEnabled"
            label={t('AbpIdentity::DisplayName:LockoutEnabled')}
          />
        </MultiStepForm.Step>

        <MultiStepForm.Step label={t('AbpIdentity::Roles')}>
          {isLoadingRoles ? (
            <Loader mt={'$5'} />
          ) : (
            <>
              {assignableRoles?.items?.map((role: IdentityRoleDto) => {
                const roleName = role.name;
                if (!roleName) return null;

                return (
                  <Row key={role.id} alignItems="center">
                    <Checkbox
                      id={`create-role-${role.id}`}
                      checked={selectedRoles.includes(roleName)}
                      onCheckedChange={() => toggleRole(roleName)}
                    >
                      <Checkbox.Indicator>
                        <Check />
                      </Checkbox.Indicator>
                    </Checkbox>
                    <Label htmlFor={`create-role-${role.id}`}>{roleName}</Label>
                  </Row>
                );
              })}
              {!assignableRoles?.items?.length ? (
                <Paragraph color={'$gray10'}>{t('::NoDataAvailableInDatatable')}</Paragraph>
              ) : null}
            </>
          )}
          <Separator my={'$2'} />
        </MultiStepForm.Step>
      </MultiStepForm>
    </FullScreenSheet>
  );
};

CreateUserSheet.displayName = 'CreateUserSheet';
